import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { authorizedAxios, useAuth } from "@moonscape/shared";

type UseUkCostBasisOptions = {
  taxYear: number;
  enabled?: boolean;
};

export type UkCostBasisResponse = {
  taxYear: number;
  taxSetting: any;
  costBasis: any[];
};

export const useUkCostBasis = ({
  taxYear,
  enabled = true,
}: UseUkCostBasisOptions): UseQueryResult<UkCostBasisResponse | null, Error> => {
  const { getToken, user } = useAuth();

  const fetchCostBasis = async (): Promise<UkCostBasisResponse | null> => {
    const token = await getToken();
    try {
      const response = await authorizedAxios(token).get(
        "/export/tax-report/uk/cost-basis",
        {
          params: { year: taxYear },
          validateStatus: (status) =>
            (status >= 200 && status < 300) || status === 204,
        }
      );

      if (response.status === 204) {
        return null;
      }

      return response.data;
    } catch (error: any) {
      const status = error?.response?.status;
      const code = error?.response?.data?.error;
      if (status === 400 && code === "unsupported_jurisdiction") {
        return null;
      }
      throw error;
    }
  };

  return useQuery({
    queryKey: ["uk_cost_basis", taxYear],
    queryFn: fetchCostBasis,
    enabled: enabled && !!user?.email && Number.isFinite(taxYear),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });
};
